import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import {
  Drawer,
  Spin,
  Button,
  Alert,
  Collapse,
  Tag,
  Form,
  Row,
  Col,
  Input,
  Select,
  Switch,
  Tabs,
  notification,
} from "antd";
import { Formik } from "formik";
import NumberFormat from "react-number-format";
import useStockItemContext from "../../../hooks/useStockItemContext";
import { useSelector } from "react-redux";
import { StyledFormContainer } from "./styles";
import StockItemJournalTab from "./StockItemJournalTab";
import {
  VETERINARY_TYPES,
  VETERINARY_APPLICATION_GENDER_TYPES,
  VETERINARY_APPLICATION_TYPES,
} from "./stockConstants";
import moment from "moment";
import { numberMask } from "../../../services/helpersMethodsService";

const { Option } = Select;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const AUDIT_FIELDS = [
  "abbreviation",
  "groupItemId",
  "itemGroupId",
  "currentCost",
];

// const NUTRIENT_UNITS = [
//   { label: "g/kg", value: "g_kg" },
//   { label: "mg/kg", value: "mg_kg" },
//   { label: "%", value: "percentage" },
// ];

// const selectNutrientUnit = (field, value, setFieldValue) => (
//   <Select
//     value={value || undefined}
//     onChange={(valueSelected) => setFieldValue(field, valueSelected)}
//   >
//     {NUTRIENT_UNITS.map((unit) => (
//       <Option key={unit.value} value={unit.value}>
//         {unit.label}
//       </Option>
//     ))}
//   </Select>
// );

// const NutritionField = ({
//   label,
//   name,
//   values,
//   setFieldValue,
//   placeholder,
// }) => {
//   const unitName = `${name}Unit`;
//   return (
//     <Form.Item label={label} colon={false}>
//       <NumberFormat
//         customInput={Input}
//         placeholder={placeholder}
//         addonAfter={selectNutrientUnit(
//           unitName,
//           values[unitName] ?? "percentage",
//           setFieldValue,
//         )}
//         value={values[name] !== null ? values[name] : ""}
//         decimalScale={4}
//         decimalSeparator=","
//         thousandSeparator="."
//         fixedDecimalScale={false}
//         onValueChange={({ floatValue }) =>
//           setFieldValue(name, floatValue !== undefined ? floatValue : null)
//         }
//       />
//     </Form.Item>
//   );
// };

const LabelValue = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <span style={{ color: "#888", fontSize: 12 }}>{label}: </span> <br />
    <strong>{value != null && value !== "" ? String(value) : "-"}</strong>
  </div>
);

const validationSchema = Yup.object({
  internalId: Yup.string().required("Campo obrigatório"),
  itemGroupId: Yup.mixed().required("Campo obrigatório"),
  measurementUnitId: Yup.mixed().required("Campo obrigatório"),
});

const StockItemDetailDrawer = () => {
  const { translation } = useSelector((state) => state.app);
  const t = translation?.stockItem || {};
  // const tf = t.form || {};
  const tStatus = translation?.status || {};
  const tl = t.drawerLabels || {};
  const tjTabs = t.journal?.tabs || {};
  // const defaultPlaceholder =
  //   translation?.defaultPlaceholder || "Informe um valor";

  const {
    isDrawerVisible,
    isLoadingEdit,
    dataItem,
    units,
    isLoadingUnits,
    suppliers,
    handleSaveItem,
    handleDeleteItem,
    handleCloseDrawer,
    loadUnits,
    loadSuppliers,
    isLoadingItemsGroups,
    itemsGroups,
    groupItems,
    isLoadingGroupItems,
    handleToggleStatus,
  } = useStockItemContext();

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (isDrawerVisible) {
      loadUnits();
      loadSuppliers();
      setIsEditing(false);
    }
  }, [isDrawerVisible, loadSuppliers, loadUnits]);

  useEffect(() => {
    if (!isDrawerVisible) {
      setIsEditing(false);
      setActiveTab("details");
    }
  }, [isDrawerVisible]);

  const getUnitAbbrev = (id) => {
    const u = units.find((u) => u.id === id);
    return u ? u.name : id || "-";
  };

  const getSupplierName = (id) => {
    const s = suppliers.find((s) => s.id === id);
    return s ? s.name || id : id || "-";
  };

  const getOptionName = (list, key) => {
    const option = list.find((o) => o.key === key);
    return option ? option.name : key || "-";
  };

  if (!dataItem && !isLoadingEdit && isDrawerVisible) {
    return (
      <Drawer
        visible={isDrawerVisible}
        width={720}
        onClose={handleCloseDrawer}
        title="Detalhes do Item"
      >
        <Spin spinning />
      </Drawer>
    );
  }

  const item = dataItem || {};
  const hasPendingAudit = !!item.hasPendingAudit;

  const handleSaveEdit = (values) => {
    const prevItem = item;
    const auditFields = AUDIT_FIELDS.filter(
      (field) => values[field] !== prevItem[field],
    ).map((field) => ({
      field,
      previousValue: prevItem[field],
      newValue: values[field],
    }));

    const payload = { ...values, auditFields };
    handleSaveItem(payload, item.id);

    // if (auditFields.length > 0) {
    //   notification.info({
    //     message: `${auditFields.length} alteração(ões) enviada(s) para aprovação. Demais campos atualizados.`,
    //   });
    // }
    setIsEditing(false);
  };

  const viewContent = (
    <>
      {hasPendingAudit && (
        <Alert
          type="warning"
          message={
            t.pendingAuditAlert ||
            "Item bloqueado — existem alterações pendentes de aprovação na auditoria."
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16}>
        <Col span={8}>
          <LabelValue
            label={tl.internalId || "Identificação"}
            value={item.internalId}
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.abbreviation || "Abreviação"}
            value={item.abbreviation}
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.description || "Descrição"}
            value={item.description}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <LabelValue label={tl.group || "Grupo"} value={item.groupItemName} />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.subgroup || "Sub-Grupo"}
            value={item.itemGroupName}
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.unit || "Unidade"}
            value={getUnitAbbrev(item.measurementUnitId)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <LabelValue
            label={tl.replenishmentPoint || "Pto Reposição"}
            value={
              item.replenishmentPoint != null
                ? Number(item.replenishmentPoint).toFixed(4)
                : "-"
            }
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.currentCost || "Custo Atual"}
            value={numberMask(item.currentCost, true, null, null, 5, 5)}
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.lastPurchaseCost || "Custo Últ. Aquis."}
            value={numberMask(item.lastPurchaseCost, true, null, null, 5, 5)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <LabelValue
            label={tl.lastPurchaseDate || "Data Últ. Aquis."}
            value={
              item.lastPurchaseDate
                ? moment(item.lastPurchaseDate).format("DD/MM/YYYY")
                : "-"
            }
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.lastSupplier || "Últ. Fornecedor"}
            value={getSupplierName(item.lastSupplierId)}
          />
        </Col>
        <Col span={8}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: "#888", fontSize: 12 }}>Status: </span>
            <Tag color={item.status === "Active" ? "green" : "red"}>
              {item.status === "Active" ? tStatus.active : tStatus.inactive}
            </Tag>
          </div>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <LabelValue
            label={tl.variantControl || "Ctrl Variante"}
            value={item.hasVariant ? "Sim" : "Não"}
          />
        </Col>
        <Col span={8}>
          <LabelValue
            label={tl.batchControl || "Ctrl Lote"}
            value={item.batchControl ? "Sim" : "Não"}
          />
        </Col>
      </Row>
      {item.notes && (
        <LabelValue label={tl.notes || "Notas"} value={item.notes} />
      )}

      {item.dietItem && (
        <Collapse style={{ marginTop: 16 }}>
          <Panel header={tl.dietItem || "Item de Dieta"} key="diet">
            <LabelValue label="Produto" value={item.product} />
            <Row gutter={8}>
              {["ndt", "pb", "ee", "fdn", "fda", "ca", "p", "k", "s", "na"].map(
                (f) => (
                  <Col key={f} span={4}>
                    <LabelValue
                      label={f.toUpperCase()}
                      value={item[f] != null ? item[f] : "-"}
                    />
                  </Col>
                ),
              )}
            </Row>
          </Panel>
        </Collapse>
      )}

      {item.veterinarySupplement && (
        <Collapse style={{ marginTop: 16 }}>
          <Panel
            header={tl.veterinarySupplement || "Item Veterinário"}
            key="veterinary"
          >
            <Row gutter={8}>
              <Col span={8}>
                <LabelValue
                  label={tl.type || "Tipo"}
                  value={getOptionName(VETERINARY_TYPES, item.type)}
                />
              </Col>
              <Col span={8}>
                <LabelValue
                  label={tl.name || "Nome Comercial"}
                  value={item.name}
                />
              </Col>
              <Col span={8}>
                <LabelValue
                  label={tl.supplierId || "Fornecedor"}
                  value={getSupplierName(item.supplierId)}
                />
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={8}>
                <LabelValue
                  label={tl.applicationGender || "Sexo"}
                  value={getOptionName(
                    VETERINARY_APPLICATION_GENDER_TYPES,
                    item.applicationGender,
                  )}
                />
              </Col>
              <Col span={8}>
                <LabelValue
                  label={tl.applicationType || "Forma de Aplicação"}
                  value={getOptionName(
                    VETERINARY_APPLICATION_TYPES,
                    item.applicationType,
                  )}
                />
              </Col>
              <Col span={8}>
                <LabelValue
                  label={tl.applicationValue || "Qtd. de Aplicação"}
                  value={item.applicationValue}
                />
              </Col>
            </Row>
            <Row gutter={8}>
              {item.applicationType !== "Fixo" && (
                <Col span={8}>
                  <LabelValue
                    label={tl.applicationWeightValueParam || "Peso"}
                    value={item.applicationWeightValueParam}
                  />
                </Col>
              )}
              <Col span={8}>
                <LabelValue
                  label={tl.unitId || "Unidade de uso"}
                  value={getUnitAbbrev(item.unitId)}
                />
              </Col>
              <Col span={8}>
                <LabelValue
                  label={tl.applicationCost || "Custo Aplicação"}
                  value={numberMask(
                    item.applicationCost,
                    true,
                    null,
                    null,
                    5,
                    2,
                  )}
                />
              </Col>
            </Row>
          </Panel>
        </Collapse>
      )}

      {((item.hasVariant &&
        Array.isArray(item.variants) &&
        item.variants.length > 0) ||
        (item.batchControl &&
          Array.isArray(item.batches) &&
          item.batches.length > 0)) && (
        <Collapse style={{ marginTop: 16 }}>
          <Panel
            header={tl.variantsAndBatches || "Variantes & Lotes"}
            key="variants"
          >
            {item.hasVariant &&
              Array.isArray(item.variants) &&
              item.variants.length > 0 && (
                <LabelValue
                  label={tl.variants || "Variantes"}
                  value={item.variants
                    .map((v) => v.internalId)
                    .filter(Boolean)
                    .join(", ")}
                />
              )}
            {item.batchControl &&
              Array.isArray(item.batches) &&
              item.batches.length > 0 && (
                <LabelValue
                  label={tl.batches || "Lotes"}
                  value={item.batches
                    .map((b) => b.batchNumber)
                    .filter(Boolean)
                    .join(", ")}
                />
              )}
          </Panel>
        </Collapse>
      )}

      <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
        {item.status === "Active" && (
          <Button
            type="primary"
            disabled={hasPendingAudit}
            onClick={() => setIsEditing(true)}
          >
            {translation.buttons.edit}
          </Button>
        )}
        {item.status === "Inactive" && (
          <Button onClick={() => handleToggleStatus(item.id, true)}>
            {translation.buttons.activate}
          </Button>
        )}
        {!item.hasMovement && item.status === "Active" && (
          <Button
            type="danger"
            onClick={() => handleDeleteItem(item.id)}
            disabled={hasPendingAudit}
          >
            {translation.buttons.inactivate}
          </Button>
        )}
      </div>
    </>
  );

  const editContent = (
    <Formik
      enableReinitialize
      initialValues={{
        internalId: item.internalId || "",
        abbreviation: item.abbreviation || "",
        description: item.description || "",
        groupItemId: item.groupItemId || null,
        itemGroupId: item.itemGroupId || null,
        measurementUnitId: item.measurementUnitId || null,
        replenishmentPoint: item.replenishmentPoint || 0,
        currentCost: item.currentCost || 0,
        hasVariant: item.hasVariant || false,
        batchControl: item.batchControl || false,
        notes: item.notes || "",
        accountingAccountId: item.accountingAccountId || null,
        dietItem: item.dietItem || false,
        product: item.product || "",
        classId: item.classId || null,
        subclassId: item.subclassId || null,
        applicationId: item.applicationId || null,
        ndt: item.ndt || null,
        pb: item.pb || null,
        dryMatterPercentage: item.dryMatterPercentage || null,
        ee: item.ee || null,
        fdn: item.fdn || null,
        fda: item.fda || null,
        ca: item.ca || null,
        p: item.p || null,
        k: item.k || null,
        s: item.s || null,
        na: item.na || null,
        veterinarySupplement: item.veterinarySupplement || false,
        type: item.type || "Vacina",
        name: item.name || "",
        supplierId: item.supplierId || null,
        applicationGender: item.applicationGender || "Both",
        applicationType: item.applicationType || "Fixo",
        applicationValue: item.applicationValue || "",
        applicationWeightValueParam: item.applicationWeightValueParam || "",
        unitId: item.unitId || null,
        applicationCost: item.applicationCost || "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSaveEdit}
    >
      {({ values, submitCount, errors, setFieldValue, handleSubmit }) => {
        // const hasError = (field) => submitCount > 0 && errors[field];
        return (
          <StyledFormContainer>
            <Form layout="vertical">
              {/* <Alert
                type="info"
                message={
                  t.auditWarning ||
                  "Campos marcados com requerem aprovação de outro usuário para entrar em vigor."
                }
                style={{ marginBottom: 16 }}
              /> */}
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Identificação">
                    <Input value={values.internalId} disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Abreviação">
                    <Input
                      value={values.abbreviation}
                      placeholder={
                        translation.defaultPlaceholder || "Informe um valor"
                      }
                      onChange={(e) =>
                        setFieldValue("abbreviation", e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Descrição">
                    <Input
                      value={values.description}
                      placeholder={
                        translation.defaultPlaceholder || "Informe um valor"
                      }
                      onChange={(e) =>
                        setFieldValue("description", e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label={"Grupo*"}>
                    <Select
                      value={values.groupItemId || undefined}
                      onChange={(val) => {
                        setFieldValue("groupItemId", val);
                        setFieldValue("itemGroupId", null);
                      }}
                      loading={isLoadingGroupItems}
                      placeholder="Selecione"
                    >
                      {groupItems.map((g) => (
                        <Option key={g.id} value={g.id}>
                          {g.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Subgrupo">
                    <Select
                      value={values.itemGroupId || undefined}
                      onChange={(val) => setFieldValue("itemGroupId", val)}
                      loading={isLoadingItemsGroups}
                      disabled={!values.groupItemId}
                      placeholder="Selecione"
                    >
                      {itemsGroups
                        .filter((sg) => sg.groupItemId === values.groupItemId)
                        .map((sg) => (
                          <Option key={sg.id} value={sg.id}>
                            {sg.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Unidade">
                    <Select
                      value={values.measurementUnitId || undefined}
                      onChange={(val) =>
                        setFieldValue("measurementUnitId", val)
                      }
                      loading={isLoadingUnits}
                      allowClear
                      placeholder="Selecione"
                    >
                      {units.map((u) => (
                        <Option key={u.id} value={u.id}>
                          {u.name || u.description}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Pto Reposição">
                    <NumberFormat
                      customInput={Input}
                      placeholder={
                        translation.defaultPlaceholder || "Informe um valor"
                      }
                      value={values.replenishmentPoint}
                      decimalScale={4}
                      decimalSeparator=","
                      thousandSeparator="."
                      fixedDecimalScale={false}
                      onValueChange={({ floatValue }) =>
                        setFieldValue(
                          "replenishmentPoint",
                          floatValue !== undefined ? floatValue : 0,
                        )
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Ctrl Variante">
                    <Switch
                      checked={values.hasVariant}
                      onChange={(val) => setFieldValue("hasVariant", val)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Ctrl Lote">
                    <Switch
                      checked={values.batchControl}
                      onChange={(val) => setFieldValue("batchControl", val)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Form.Item label="Notas">
                  <Input.TextArea
                    value={values.notes}
                    placeholder={
                      translation.defaultPlaceholder || "Informe um valor"
                    }
                    rows={2}
                    onChange={(e) => setFieldValue("notes", e.target.value)}
                  />
                </Form.Item>
              </Row>
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <Button
                  type="primary"
                  loading={isLoadingEdit}
                  onClick={handleSubmit}
                >
                  {translation.buttons.save}
                </Button>
                <Button onClick={() => setIsEditing(false)}>
                  {translation.buttons.cancel}
                </Button>
              </div>
            </Form>
          </StyledFormContainer>
        );
      }}
    </Formik>
  );

  return (
    <Drawer
      visible={isDrawerVisible}
      width={720}
      onClose={handleCloseDrawer}
      title={
        isEditing
          ? `Editando: ${item.internalId || ""}`
          : `${item.internalId || ""}${item.abbreviation ? ` - ${item.abbreviation}` : ""}`
      }
    >
      <Spin spinning={isLoadingEdit}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={tjTabs.details || "Detalhes"} key="details">
            {isEditing ? editContent : viewContent}
          </TabPane>
          <TabPane tab={tjTabs.movements || "Movimentações"} key="journal">
            {activeTab === "journal" && (
              <StockItemJournalTab key={item.id} itemId={item.id} />
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </Drawer>
  );
};

export default StockItemDetailDrawer;
